import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, EyeOff, Eye } from 'lucide-react';

export default function KPICards({ filteredPlans, projects, components, mdpData, periodMdp, weekLabel }) {
  const [mainOpen, setMainOpen] = useState(false);
  const [trainOpen, setTrainOpen] = useState(true);
  const [coachOpen, setCoachOpen] = useState(true);
  const [busOpen, setBusOpen] = useState(true);
  const [hideCompleted, setHideCompleted] = useState(true);

  const stats = useMemo(() => {
    const cats = {
      Train: { planned: 0, completed: 0, shipped: 0, notShipped: 0, periodMdp: 0, remaining: 0, shortageProd: 0, shortageShip: 0, plannedShortage: 0 },
    };

    const setProjectStats = {};
    projects.forEach(proj => {
      if (proj.planning_unit !== 'set') return;
      const ordered = proj.ordered_quantity || 0;
      const planned = proj.total_planned || 0;
      const completed = proj.total_completed || 0;
      const shipped = proj.total_shipped || 0;
      setProjectStats[proj.id] = {
        id: proj.id, name: proj.name, category: proj.category || 'Coach',
        sr_number: proj.sr_number || '',
        seat_type_name: proj.seat_type_name || '',
        ordered, planned, completed, shipped,
        notShipped: Math.max(0, completed - shipped),
        remaining: Math.max(0, ordered - shipped),
        has_nemo: proj.has_nemo,
        nemo_seat_type: proj.nemo_seat_type || '',
      };
    });

    filteredPlans.forEach(p => {
      const proj = projects.find(pr => pr.id === p.project_id);
      const cat = proj?.category || p.category_name || 'Train';
      if (p.planning_unit === 'seat') {
        if (!cats[cat]) cats[cat] = { planned: 0, completed: 0, shipped: 0, notShipped: 0, periodMdp: 0, remaining: 0 };
        const spu = p.seats_per_unit || 0;
        cats[cat].planned += (p.planned_quantity || 0) * spu;
        cats[cat].completed += (p.completed_quantity || 0) * spu;
        cats[cat].shipped += (p.shipped_quantity || 0) * spu;
      }
    });

    let trainMdpUsed = false;
    projects.forEach(proj => {
      const cat = proj.category;
      if (proj.planning_unit === 'seat' && periodMdp && periodMdp[proj.id]) {
        if (!cats[cat]) return;
        const kpi = periodMdp[proj.id].kpi;
        cats[cat].periodMdp = kpi?.period_mdp_seats || 0;
        cats[cat].periodSeatPlaced = kpi?.period_seat_placed || 0;
        cats[cat].cumulativeSeatPlaced = kpi?.cumulative_mdp_seat_placed || 0;
        cats[cat].remaining = kpi?.remaining_seat_placed || 0;
        cats[cat].shortageProd = kpi?.shortage_produced_seat_placed || 0;
        cats[cat].shortageShip = kpi?.shortage_shipped_seat_placed || 0;
        cats[cat].plannedShortage = kpi?.planned_shortage_seat_placed || 0;
        cats[cat].cumShipped = kpi?.shipped_seat_placed || 0;
        cats[cat].shortage2p = kpi?.shortage_2p_seats || 0;
        cats[cat].shortage1p = kpi?.shortage_1p_seats || 0;
        cats[cat].notShipped = Math.max(0, cats[cat].completed - cats[cat].shipped);
        trainMdpUsed = true;
      }
    });

    if (!trainMdpUsed && mdpData) {
      projects.forEach(proj => {
        const cat = proj.category;
        if (cats[cat] && proj.planning_unit === 'seat' && mdpData[proj.id]) {
          const projMdp = mdpData[proj.id];
          let mdpOrdered = 0, mdpShipped = 0;
          Object.values(projMdp).forEach(m => {
            if (m.component_type === 'seat') {
              mdpOrdered += m.mdp * m.sittings_per_unit;
              mdpShipped += m.shipped * m.sittings_per_unit;
            }
          });
          cats[cat].periodMdp = mdpOrdered;
          cats[cat].shipped = mdpShipped;
          cats[cat].remaining = Math.max(0, mdpOrdered - cats[cat].completed);
          cats[cat].notShipped = Math.max(0, cats[cat].completed - cats[cat].shipped);
        }
      });
    }

    const allCoachProjects = Object.values(setProjectStats).filter(p => p.category === 'Coach');
    const allBusProjects = Object.values(setProjectStats).filter(p => p.category === 'City Bus');
    const isProjectCompleted = (p) => p.ordered > 0 && p.shipped >= p.ordered;
    const coachProjects = hideCompleted ? allCoachProjects.filter(p => !isProjectCompleted(p)) : allCoachProjects;
    const busProjects = hideCompleted ? allBusProjects.filter(p => !isProjectCompleted(p)) : allBusProjects;
    const hiddenCoachCount = allCoachProjects.length - coachProjects.length;
    const hiddenBusCount = allBusProjects.length - busProjects.length;

    const sumSetGroup = (arr) => arr.reduce((acc, p) => ({
      ordered: acc.ordered + p.ordered, planned: acc.planned + p.planned,
      completed: acc.completed + p.completed, shipped: acc.shipped + p.shipped,
      notShipped: acc.notShipped + p.notShipped, remaining: acc.remaining + p.remaining,
    }), { ordered: 0, planned: 0, completed: 0, shipped: 0, notShipped: 0, remaining: 0 });

    const coachTotals = sumSetGroup(coachProjects);
    const busTotals = sumSetGroup(busProjects);
    const totalSets = sumSetGroup([...coachProjects, ...busProjects]);
    const trainProjects = projects.filter(p => p.planning_unit === 'seat').map(p => ({
      id: p.id, name: p.name, sr_number: p.sr_number || '', seat_type_name: p.seat_type_name || '',
    }));

    // Component shortage for City Bus (from filteredPlans)
    const busCompShortage = { flip_up: { notDone: 0, notShipped: 0 }, barrier: { notDone: 0, notShipped: 0 }, trackedSets: 0 };
    filteredPlans.forEach(p => {
      if (p.set_number && p.track_components && p.component_status) {
        busCompShortage.trackedSets++;
        ['flip_up', 'barrier'].forEach(ck => {
          const cs = p.component_status[ck] || {};
          if (!cs.done) busCompShortage[ck].notDone++;
          if (!cs.shipped) busCompShortage[ck].notShipped++;
        });
      }
    });

    return { cats, coachProjects, busProjects, coachTotals, busTotals, totalSets, trainProjects, hiddenCoachCount, hiddenBusCount, allCoachProjects, allBusProjects, busCompShortage };
  }, [filteredPlans, projects, components, mdpData, periodMdp, hideCompleted]);

  const t = stats.cats.Train;
  const trainProjects = stats.trainProjects || [];
  const pctBase = t.cumulativeSeatPlaced || 0;
  const trainPct = pctBase > 0 ? Math.round((t.completed / pctBase) * 100) : 0;

  const TRAIN_GRID = 'grid grid-cols-[100px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-0';
  const SET_GRID = 'grid grid-cols-[100px_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-0';

  const SectionToggle = ({ label, isOpen, onToggle, color, count }) => (
    <button onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
      data-testid={`toggle-${label.toLowerCase().replace(/\s/g, '-')}`}>
      {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-xs font-bold text-slate-700">{label}</span>
      {count !== undefined && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-semibold">{count}</span>}
    </button>
  );

  const SetDataRow = ({ label, data, dot, isSub, isBold, srNumber, seatType }) => {
    const pct = (data.ordered || 0) > 0 ? Math.round((data.completed / data.ordered) * 100) : 0;
    return (
      <div className={`${SET_GRID} ${isBold ? 'bg-orange-50/50 border-t border-slate-200' : isSub ? 'bg-white' : 'bg-slate-50/30'}`}>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} flex items-start gap-1.5 ${isSub ? 'pl-5' : ''}`}>
          <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${dot}`} />
          <div className="min-w-0">
            <span className={`text-xs ${isBold ? 'font-bold text-slate-800' : isSub ? 'font-medium text-slate-500' : 'font-semibold text-slate-600'} block truncate`}>{label}</span>
            {isSub && srNumber && <span className="text-[8px] text-blue-500 font-medium block">{srNumber}</span>}
            {isSub && seatType && <span className="text-[8px] text-slate-400 block">{seatType}</span>}
          </div>
        </div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} text-amber-600`}>{data.ordered || '-'}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} text-blue-600`}>{data.planned}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} text-emerald-600`}>{data.completed}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} text-violet-600`}>{data.shipped}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} ${data.notShipped > 0 ? 'text-orange-500' : 'text-slate-300'}`}>{data.notShipped}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold' : 'font-semibold'} ${pct >= 100 ? 'text-emerald-600' : pct > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{isBold ? '' : `${pct}%`}</span></div>
        <div className={`px-2 py-${isSub ? '1.5' : '2.5'} text-center`}><span className={`${isSub ? 'text-xs' : 'text-sm'} ${isBold ? 'font-bold text-red-500' : 'font-semibold'} ${!isBold && data.remaining > 0 ? 'text-red-500' : !isBold ? 'text-slate-300' : ''}`}>{data.remaining > 0 ? data.remaining.toLocaleString() : '0'}</span></div>
      </div>
    );
  };

  // Quick summary for collapsed state

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="planning-kpis">
      {/* Main toggle header */}
      <div className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200 cursor-pointer"
        onClick={() => setMainOpen(!mainOpen)}
        data-testid="kpi-main-toggle">
        <div className="flex items-center gap-2">
          {mainOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <span className="text-sm font-bold text-slate-700">KPI Summary</span>
          {weekLabel && <span className="text-[10px] text-slate-400 font-medium">- {weekLabel}</span>}
        </div>
        <div className="flex items-center gap-3">
          {mainOpen && (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5">
              <button onClick={() => setHideCompleted(h => !h)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-all border ${hideCompleted ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                data-testid="kpi-hide-completed-toggle">
                {hideCompleted ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {hideCompleted ? 'Completed Hidden' : 'Show All'}
                {hideCompleted && (stats.hiddenCoachCount + stats.hiddenBusCount) > 0 && (
                  <span className="bg-amber-200 text-amber-800 px-1 py-0 rounded text-[9px]">{stats.hiddenCoachCount + stats.hiddenBusCount}</span>
                )}
              </button>
            </div>
          )}
          {!mainOpen && (
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-slate-400 font-medium italic">Click to expand</span>
            </div>
          )}
        </div>
      </div>

      {mainOpen && (
        <div className="divide-y divide-slate-100">
          {/* ─── TRAIN ─── */}
          <div>
            <SectionToggle label="Train" isOpen={trainOpen} onToggle={() => setTrainOpen(!trainOpen)} color="bg-blue-500" count={trainProjects.length} />
            {trainOpen && (
              <div className="border-t border-slate-100">
                <div className={`${TRAIN_GRID} text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-blue-50/30 border-b border-slate-100`}>
                  <div className="px-2 py-1.5">
                    <span className="text-[7px] normal-case font-semibold text-blue-500">Weekly Summary</span>
                  </div>
                  <div className="px-2 py-1.5 text-center">MDP<br/><span className="text-[7px] normal-case text-amber-500">seat</span></div>
                  <div className="px-2 py-1.5 text-center">Planned<br/><span className="text-[7px] normal-case text-blue-400">sp</span></div>
                  <div className="px-2 py-1.5 text-center">Completed<br/><span className="text-[7px] normal-case text-emerald-400">sp</span></div>
                  <div className="px-2 py-1.5 text-center">Shipped<br/><span className="text-[7px] normal-case text-violet-400">sp</span></div>
                  <div className="px-2 py-1.5 text-center">Cum. Ship<br/><span className="text-[7px] normal-case text-indigo-400">sp</span></div>
                  <div className="px-2 py-1.5 text-center text-red-400">S.PRD<br/><span className="text-[7px] normal-case">sp</span></div>
                  <div className="px-2 py-1.5 text-center text-red-400">S.SHP<br/><span className="text-[7px] normal-case">sp</span></div>
                  <div className="px-2 py-1.5 text-center text-orange-400">S.PLN<br/><span className="text-[7px] normal-case">sp</span></div>
                  <div className="px-2 py-1.5 text-center">Completion</div>
                  <div className="px-2 py-1.5 text-center">Remaining<br/><span className="text-[7px] normal-case">sp</span></div>
                  <div className="px-2 py-1.5 text-center">Shortage<br/><span className="text-[7px] normal-case text-red-300">2P / 1P</span></div>
                </div>
                <div className={`${TRAIN_GRID} bg-white`}>
                  <div className="px-2 py-2 flex items-start gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500 mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      {trainProjects.map((tp, i) => (
                        <div key={tp.id} className={i > 0 ? 'mt-1 pt-1 border-t border-slate-100' : ''}>
                          <span className="text-xs font-semibold text-slate-600 block truncate">{tp.name}</span>
                          {tp.sr_number && <span className="text-[8px] text-blue-500 font-medium block">{tp.sr_number}</span>}
                          {tp.seat_type_name && <span className="text-[8px] text-slate-400 block">{tp.seat_type_name}</span>}
                        </div>
                      ))}
                      {trainProjects.length === 0 && <span className="text-xs font-semibold text-slate-600">Train</span>}
                    </div>
                  </div>
                  <div className="px-2 py-2 text-center"><span className="text-sm font-semibold text-amber-600">{t.periodMdp || '-'}</span></div>
                  <div className="px-2 py-2 text-center"><span className="text-sm font-semibold text-blue-600">{t.planned}</span></div>
                  <div className="px-2 py-2 text-center"><span className="text-sm font-semibold text-emerald-600">{t.completed}</span></div>
                  <div className="px-2 py-2 text-center"><span className="text-sm font-semibold text-violet-600">{t.shipped}</span></div>
                  <div className="px-2 py-2 text-center"><span className="text-sm font-semibold text-indigo-600">{(t.cumShipped || 0).toLocaleString()}</span></div>
                  <div className="px-2 py-2 text-center"><span className={`text-sm font-semibold ${t.shortageProd > 0 ? 'text-red-500' : 'text-slate-300'}`}>{t.shortageProd}</span></div>
                  <div className="px-2 py-2 text-center"><span className={`text-sm font-semibold ${t.shortageShip > 0 ? 'text-red-500' : 'text-slate-300'}`}>{t.shortageShip}</span></div>
                  <div className="px-2 py-2 text-center"><span className={`text-sm font-semibold ${t.plannedShortage > 0 ? 'text-orange-500' : 'text-slate-300'}`}>{t.plannedShortage}</span></div>
                  <div className="px-2 py-2 text-center"><span className={`text-sm font-semibold ${trainPct >= 100 ? 'text-emerald-600' : trainPct > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{trainPct}%</span></div>
                  <div className="px-2 py-2 text-center"><span className={`text-sm font-semibold ${t.remaining > 0 ? 'text-red-500' : 'text-slate-300'}`}>{t.remaining > 0 ? t.remaining.toLocaleString() : '0'}</span></div>
                  <div className="px-2 py-2 text-center">
                    <span className="text-[10px] font-bold">
                      <span className="text-red-500">{t.shortage2p || 0}</span>
                      <span className="text-slate-300"> / </span>
                      <span className="text-orange-500">{t.shortage1p || 0}</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── COACH ─── */}
          <div>
            <SectionToggle label="Coach" isOpen={coachOpen} onToggle={() => setCoachOpen(!coachOpen)} color="bg-orange-500" count={stats.coachProjects.length} />
            {coachOpen && (
              <div className="border-t border-slate-100">
                <div className={`${SET_GRID} text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-orange-50/30 border-b border-slate-100`}>
                  <div className="px-2 py-1.5"><span className="text-[7px] normal-case font-semibold text-orange-500">Overall</span></div>
                  <div className="px-2 py-1.5 text-center">Ordered</div>
                  <div className="px-2 py-1.5 text-center">Planned</div>
                  <div className="px-2 py-1.5 text-center">Completed</div>
                  <div className="px-2 py-1.5 text-center">Shipped</div>
                  <div className="px-2 py-1.5 text-center">Not Shipped</div>
                  <div className="px-2 py-1.5 text-center">Completion</div>
                  <div className="px-2 py-1.5 text-center">Remaining</div>
                </div>
                <SetDataRow label="Coach" data={stats.coachTotals} dot="bg-orange-500" />
                {stats.coachProjects.map(p => (
                  <SetDataRow key={p.id} label={p.name} data={p} dot="bg-orange-300" isSub srNumber={p.sr_number} seatType={p.seat_type_name} />
                ))}
                {hideCompleted && stats.hiddenCoachCount > 0 && (
                  <div className="px-3 py-1.5 text-[10px] text-slate-400 italic bg-slate-50/50 border-t border-slate-100">
                    {stats.hiddenCoachCount} completed project{stats.hiddenCoachCount > 1 ? 's' : ''} hidden
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── CITY BUS ─── */}
          <div>
            <SectionToggle label="City Bus" isOpen={busOpen} onToggle={() => setBusOpen(!busOpen)} color="bg-green-500" count={stats.busProjects.length} />
            {busOpen && (
              <div className="border-t border-slate-100">
                <div className={`${SET_GRID} text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-green-50/30 border-b border-slate-100`}>
                  <div className="px-2 py-1.5"><span className="text-[7px] normal-case font-semibold text-green-500">Overall</span></div>
                  <div className="px-2 py-1.5 text-center">Ordered</div>
                  <div className="px-2 py-1.5 text-center">Planned</div>
                  <div className="px-2 py-1.5 text-center">Completed</div>
                  <div className="px-2 py-1.5 text-center">Shipped</div>
                  <div className="px-2 py-1.5 text-center">Not Shipped</div>
                  <div className="px-2 py-1.5 text-center">Completion</div>
                  <div className="px-2 py-1.5 text-center">Remaining</div>
                </div>
                <SetDataRow label="City Bus" data={stats.busTotals} dot="bg-green-500" />
                {stats.busProjects.map(p => (
                  <SetDataRow key={p.id} label={p.name} data={p} dot="bg-green-300" isSub srNumber={p.sr_number} seatType={p.seat_type_name} />
                ))}
                {hideCompleted && stats.hiddenBusCount > 0 && (
                  <div className="px-3 py-1.5 text-[10px] text-slate-400 italic bg-slate-50/50 border-t border-slate-100">
                    {stats.hiddenBusCount} completed project{stats.hiddenBusCount > 1 ? 's' : ''} hidden
                  </div>
                )}
                {stats.busCompShortage.trackedSets > 0 && (stats.busCompShortage.flip_up.notDone > 0 || stats.busCompShortage.barrier.notDone > 0) && (
                  <div className="px-3 py-2 bg-amber-50/70 border-t border-amber-200 text-[10px]" data-testid="kpi-bus-comp-shortage">
                    <span className="font-bold text-amber-700">Component Shortage ({stats.busCompShortage.trackedSets} tracked sets):</span>
                    <div className="flex gap-4 mt-1">
                      <span className="text-slate-600">Flip-Up: <span className="font-bold text-red-500">{stats.busCompShortage.flip_up.notDone} not done</span>, <span className="font-bold text-orange-500">{stats.busCompShortage.flip_up.notShipped} not shipped</span></span>
                      <span className="text-slate-600">Barrier: <span className="font-bold text-red-500">{stats.busCompShortage.barrier.notDone} not done</span>, <span className="font-bold text-orange-500">{stats.busCompShortage.barrier.notShipped} not shipped</span></span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── TOTAL ─── */}
          <div className="bg-orange-50/50">
            <SetDataRow label="Total" data={stats.totalSets} dot="bg-orange-700" isBold />
          </div>
        </div>
      )}
    </div>
  );
}
